import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { Box, Grid, IconButton, TextField, Typography } from '@material-ui/core';
import { Add, Close } from '@material-ui/icons';
import { v4 as uuidv4 } from 'uuid';
import { useCampfireTheme } from '../../../../../../../../../theme/useCampfireTheme';

type MatrixRow = {
  matrixRowId: string;
  name: string;
  order: number;
};

type MatrixColumn = {
  matrixColumnId: string;
  name: string;
  order: number;
};

type MatrixValues = {
  field: {
    rows: MatrixRow[];
    columns: MatrixColumn[];
  };
};

export const MatrixAdditionalFields = () => {
  const { values, setFieldValue } = useFormikContext<MatrixValues>();
  const { theme } = useCampfireTheme();

  const rows = values.field.rows || [];
  const columns = values.field.columns || [];

  useEffect(() => {
    if (rows.length === 0) {
      addRow();
    }
    if (columns.length === 0) {
      addColumn();
    }
  }, []);

  const addRow = () => {
    const newRow: MatrixRow = {
      matrixRowId: `new-${uuidv4()}`,
      name: '',
      order: rows.length,
    };
    setFieldValue('field.rows', [...rows, newRow]);
  };

  const addColumn = () => {
    const newColumn: MatrixColumn = {
      matrixColumnId: `new-${uuidv4()}`,
      name: '',
      order: columns.length,
    };
    setFieldValue('field.columns', [...columns, newColumn]);
  };

  const removeRow = (rowId: string) => {
    setFieldValue(
      'field.rows',
      rows.filter((r) => r.matrixRowId !== rowId).map((r, i) => ({ ...r, order: i }))
    );
  };

  const removeColumn = (columnId: string) => {
    setFieldValue(
      'field.columns',
      columns.filter((c) => c.matrixColumnId !== columnId).map((c, i) => ({ ...c, order: i }))
    );
  };

  const updateRowName = (rowId: string, name: string) => {
    setFieldValue(
      'field.rows',
      rows.map((r) => (r.matrixRowId === rowId ? { ...r, name } : r))
    );
  };

  const updateColumnName = (columnId: string, name: string) => {
    setFieldValue(
      'field.columns',
      columns.map((c) => (c.matrixColumnId === columnId ? { ...c, name } : c))
    );
  };

  return (
    <Box>
      <Box mb={2}>
        <Typography variant='body2' color='textSecondary' style={{ marginBottom: 8 }}>
          Rows (Questions)
        </Typography>
        {rows.length === 0 && (
          <Box border={`1px solid ${theme.status.red.medium}`} borderRadius={4} p={1} mb={1} textAlign='center'>
            <Typography variant='body2' color='error'>
              At least one row is required
            </Typography>
          </Box>
        )}
        {rows.map((row, index) => (
          <Grid container spacing={1} alignItems='center' key={row.matrixRowId} style={{ marginBottom: 4 }}>
            <Grid item xs>
              <TextField
                fullWidth
                variant='outlined'
                size='small'
                placeholder={`Row ${index + 1}`}
                value={row.name}
                onChange={(e) => updateRowName(row.matrixRowId, e.target.value)}
              />
            </Grid>
            <Grid item>
              <IconButton size='small' onClick={() => removeRow(row.matrixRowId)}>
                <Close fontSize='small' />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Box mt={1}>
          <IconButton size='small' color='primary' onClick={addRow}>
            <Add fontSize='small' />
          </IconButton>
          <Typography variant='body2' color='primary' component='span' style={{ cursor: 'pointer' }} onClick={addRow}>
            Add row
          </Typography>
        </Box>
      </Box>

      <Box>
        <Typography variant='body2' color='textSecondary' style={{ marginBottom: 8 }}>
          Columns (Response Options)
        </Typography>
        {columns.length === 0 && (
          <Box border={`1px solid ${theme.status.red.medium}`} borderRadius={4} p={1} mb={1} textAlign='center'>
            <Typography variant='body2' color='error'>
              At least one column is required
            </Typography>
          </Box>
        )}
        {columns.map((column, index) => (
          <Grid container spacing={1} alignItems='center' key={column.matrixColumnId} style={{ marginBottom: 4 }}>
            <Grid item xs>
              <TextField
                fullWidth
                variant='outlined'
                size='small'
                placeholder={`Column ${index + 1}`}
                value={column.name}
                onChange={(e) => updateColumnName(column.matrixColumnId, e.target.value)}
              />
            </Grid>
            <Grid item>
              <IconButton size='small' onClick={() => removeColumn(column.matrixColumnId)}>
                <Close fontSize='small' />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Box mt={1}>
          <IconButton size='small' color='primary' onClick={addColumn}>
            <Add fontSize='small' />
          </IconButton>
          <Typography
            variant='body2'
            color='primary'
            component='span'
            style={{ cursor: 'pointer' }}
            onClick={addColumn}
          >
            Add column
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
